/**
 * The log of a game with the rules engine, composed from the events' data ( not their English
 * text ) : short names, grouped lines. Seats are "P1", "P2"… ; a vampire is the first word of its
 * name when no other name seen starts with it. Draws, passes and failures are left out ; a seat's
 * influence phase is one line ; an action is one line ( what it is, then how it ended ) folding the
 * plays, blocks and votes during it ; a combat in it folds its strike rounds, one line each. Pool
 * and blood moved are told on the line of the move that moved them.
 */

import type {
    ActionKind,
    ActionRef,
    Card,
    CardRef,
    Cause,
    Event,
    OptionView,
    SlotRef,
    StruckSide,
    TableView,
    Target,
    Window,
} from '@/client/engine/api.ts'
import { actionName, effectsText, signed, strikeName } from '@/client/engine/labels.ts'
import type { TextSegment } from '@/client/engine/cardNames.ts'
import { isCryptId } from '@/shared/model/Card.ts'

// A piece of a log line : text, a card's name, or a seat ( "P2", in its color )
export type LogSegment = TextSegment & { seat?: number }

export type EngineLogEntry = {
    id: number
    // Its author's seat ; null for the table ( a turn, a combat )
    seat: number | null
    segments: LogSegment[]
    // The lines it folds : an action's plays, blocks, votes and combat ; a combat's rounds
    children: EngineLogEntry[]
    // An action or a combat under way
    live: boolean
    // The same line in a log rebuilt after an undo : the user's folding kept by it
    key: string
    time: Date
}

// How an action reads after its minion : "Theo bleeds P3", "Anson calls Kine Resources Contested"
const VERBS: Partial<Record<ActionKind, string>> = {
    bleed: 'bleeds',
    hunt: 'hunts',
    leave_torpor: 'leaves torpor',
    rescue: 'rescues',
    diablerie: 'diablerizes',
    employ: 'employs',
    recruit: 'recruits',
    political: 'calls',
    card: 'plays',
    burn_card: 'burns',
    steal_card: 'steals',
    equip: 'equips',
    equip_from: 'equips from',
    equip_found: 'equips',
    ability: 'acts',
    become_anarch: 'becomes Anarch',
    red_list_hunt: 'hunts',
    power: 'gets a Power',
    burn_imbued: 'burns',
}

const OUTCOMES: Record<string, string> = {
    succeeds: 'succeeds',
    blocked: 'blocked',
    fails: 'fails',
    fizzles: 'fizzles',
    ousted: 'ends, its target ousted',
    ends: 'ends',
}

// An English line told after another : its first letter lowered
function lowered(text: string): string {
    return text.charAt(0).toLowerCase() + text.slice(1)
}

// The fields naming a minion or card in play by its copy ( a card's own id is `id`, or `card` as a
// number )
const COPIES = ['copy', 'minion', 'acting', 'actor', 'blocker', 'on', 'hunted', 'calling', 'from']

// The words a name's short form skips : articles and titles ( "Doctor Morocco" is Morocco )
const TITLES =
    /^(the|la|le|el|doctor|dr\.?|sir|lady|lord|dame|madame|mister|mr\.?|mrs\.?|miss|father|mother|sister|brother|saint|st\.?|prince|princess|king|queen|baron|baroness|count|countess|duke|duchess|captain|general|professor|bishop|archbishop|cardinal|abbot|uncle|aunt)$/i

// A name's first word that is not an article or a title ( the last one, if all are ) ; quotes and
// commas left out
function firstWord(name: string): string {
    const words = name.replace(/[",]/g, '').split(/\s+/)
    return words.find(word => !TITLES.test(word)) ?? words[words.length - 1]
}

type Known = { id: number; numbered?: number | null }
// An uncontrolled vampire : by its copy when the viewer sees it, else by its place
type Uncontrolled = { copy?: number; slot?: SlotRef; n: number }
type Influence = {
    entry: EngineLogEntry
    seat: number
    // Pool transferred ; blood moved back to the pool : by vampire
    pool: Map<string, Uncontrolled>
    back: Map<string, Uncontrolled>
    // Influenced out ( its card, told as it enters play ) ; crypt cards drawn
    out: Card[]
    crypt: number
}
type Action = {
    entry: EngineLogEntry
    acting: number
    referendum: boolean
    card?: number
    // The action card's copy : a card action's effects name it as their cause
    cardCopy?: number
    // The Methuselah bled, and the pool the bleed took ( from another, redirected )
    bled?: number
    pooled?: number
    pooledSeat?: number
}

// What the log was told, in order : replayed after an undo
type Told = { event: Event; at: Date } | { say: { seat: number; text: string }; at: Date }

export class EngineLogBuilder {
    private nextId = 1
    private told: Told[] = []
    // When what is told now was told : a line keeps its time through a rebuild
    private at = new Date()
    // The lines told at the decision under way : a line's key
    private lines = 0
    // The minions and cards met, by copy
    private copies = new Map<number, Known>()
    // The crypt cards' names met : a short name is the first word only when no other starts with it
    private cryptNames = new Set<string>()
    private seatNames: string[] = []
    // The seat the log is told to
    private viewer: number | null = null
    private decision = 0
    private influence: Influence | null = null
    private action: Action | null = null
    private combat: EngineLogEntry | null = null
    // The last round's strikes end the combat
    private endingStrike = false
    // The line the next pool, blood and card movements are told on ; and the card it plays, by copy
    private current: EngineLogEntry | null = null
    private currentCopy: number | null = null
    // Block attempts under way, by the blocking minion : a failed one with no card played since is
    // taken out of the log
    private blocks = new Map<number, { entry: EngineLogEntry; parent: EngineLogEntry[] }>()
    // Cards played, by copy, until they resolve : what they do told on their line
    private plays = new Map<number, EngineLogEntry>()
    // A card played from a hand that hides it : its line, until its move to limbo tells its copy
    private playing: { id: number; entry: EngineLogEntry } | null = null
    // The action decided, until the engine announces it : its target and card, the terms chosen
    // meanwhile
    private declaring: {
        seat: number
        choice: OptionView
        terms: { told: string; choice: OptionView }[]
    } | null = null
    // The card just resolved : the pool and blood it moves, told on its line
    private resolving: { entry: EngineLogEntry; copy: number; told: boolean } | null = null

    constructor(
        // The log's lines : a reactive array, its entries changed through it
        private root: EngineLogEntry[],
        private cardName: (id: number) => string | undefined,
        // Told of every line added or grown
        private changed: () => void = () => {},
    ) {}

    // The table as it stands : the minions and cards it shows, by copy
    learn(view: TableView) {
        this.seatNames = view.seats.map(seat => seat.name)
        this.viewer = view.viewer ?? null
        for (const seat of view.seats) {
            for (const minion of [...seat.ready, ...seat.torpor, ...(seat.incapacitated ?? [])]) {
                this.know(minion.card)
                for (const card of minion.cards) {
                    this.know(card.card)
                }
            }
            for (const slot of seat.uncontrolled) {
                this.know(slot.card)
            }
            for (const card of [...seat.ash_heap, ...seat.in_play.map(c => c.card)]) {
                this.know(card)
            }
        }
    }

    private know(card: Card | null | undefined) {
        if (!card || card.id == null) {
            return
        }
        const crypt = isCryptId(card.id)
        if (card.copy != null) {
            const known = this.copies.get(card.copy)
            this.copies.set(card.copy, { id: card.id, numbered: card.numbered ?? known?.numbered })
        }
        const name = this.cardName(card.id)
        if (name && crypt) {
            this.cryptNames.add(name)
        }
    }

    // A vampire's short name : its first word, unless another vampire met starts with it
    shortName(name: string): string {
        const first = firstWord(name)
        for (const other of this.cryptNames) {
            if (other != name && firstWord(other) == first) {
                return name
            }
        }
        return first
    }

    private cardSegment(id: number | null | undefined, numbered?: number | null): LogSegment {
        const name = id != null ? this.cardName(id) : undefined
        if (id == null || !name) {
            return { text: 'a card' }
        }
        const crypt = isCryptId(id)
        const shown = crypt ? this.shortName(name) : name
        return {
            text: numbered ? `${shown} (${numbered})` : shown,
            krcgId: String(id),
            isCrypt: crypt,
        }
    }

    // A minion or card in play, by copy
    private copySegment(copy: number | null | undefined): LogSegment {
        const known = copy != null ? this.copies.get(copy) : undefined
        if (!known) {
            return { text: 'a minion' }
        }
        return this.cardSegment(known.id, known.numbered)
    }

    private seatSegment(seat: number): LogSegment {
        return { text: `P${seat + 1}`, seat }
    }

    // An English line of the engine, its names shortened : what the data does not say better. The
    // names linked are those its event or option names ( `data` ), never others the text may hold
    private fromText(text: string, data?: unknown): LogSegment[] {
        const names = new Map<string, LogSegment>()
        this.named(data, names)
        if (names.size == 0) {
            return [{ text }]
        }
        const matcher = new RegExp(
            `(?<!\\w)(?:${[...names.keys()]
                .sort((a, b) => b.length - a.length)
                .map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
                .join('|')})(?!\\w)`,
            'g',
        )
        const segments: LogSegment[] = []
        let last = 0
        for (const match of text.matchAll(matcher)) {
            const segment = names.get(match[0])
            if (match.index === undefined || !segment) {
                continue
            }
            if (match.index > last) {
                segments.push({ text: text.slice(last, match.index) })
            }
            segments.push(segment)
            last = match.index + match[0].length
        }
        if (last < text.length) {
            segments.push({ text: text.slice(last) })
        }
        return segments
    }

    // The cards and Methuselahs some data names, by the name its English uses ( a card's full name )
    private named(data: unknown, names: Map<string, LogSegment>) {
        if (Array.isArray(data)) {
            data.forEach(item => this.named(item, names))
            return
        }
        if (!data || typeof data != 'object') {
            return
        }
        const card = (id: number) => {
            const name = this.cardName(id)
            if (name) {
                names.set(name, this.cardSegment(id))
            }
        }
        const seat = (n: number) => {
            const name = this.seatNames[n]
            if (name) {
                names.set(name, this.seatSegment(n))
            }
        }
        for (const [key, value] of Object.entries(data)) {
            if (typeof value == 'number') {
                if (key == 'id' || key == 'card') {
                    card(value)
                } else if (COPIES.includes(key)) {
                    const known = this.copies.get(value)
                    if (known) {
                        card(known.id)
                    }
                } else if (key == 'seat') {
                    seat(value)
                }
            } else if (key == 'seats' && Array.isArray(value)) {
                value.forEach(n => typeof n == 'number' && seat(n))
            } else {
                this.named(value, names)
            }
        }
    }

    private line(
        seat: number | null,
        segments: LogSegment[],
        parent: EngineLogEntry[] = this.container(),
    ): EngineLogEntry {
        parent.push({
            id: this.nextId++,
            seat,
            segments,
            children: [],
            live: false,
            key: `${this.decision}.${this.lines++}`,
            time: this.at,
        })
        this.changed()
        // The reactive entry, to change it later
        return parent[parent.length - 1]
    }

    // Where a line goes : in the combat under way, else in the action, else the log
    private container(): EngineLogEntry[] {
        return this.combat?.children ?? this.action?.entry.children ?? this.root
    }

    // Told after a line : "…, P3 −2 pool"
    private append(entry: EngineLogEntry, segments: LogSegment[], separator = ', ') {
        if (segments.length == 0) {
            return
        }
        entry.segments.push({ text: separator }, ...segments)
        this.changed()
    }

    // What happened, told on the current line, else a table line
    private tell(segments: LogSegment[]) {
        if (this.current) {
            this.append(this.current, segments)
        } else {
            this.current = this.line(null, segments)
        }
    }

    // A line of the driver's own ( an undo )
    say(seat: number, text: string) {
        this.at = new Date()
        this.told.push({ say: { seat, text }, at: this.at })
        this.said(seat, text)
    }

    private said(seat: number, text: string) {
        this.closeInfluence()
        this.current = this.line(seat, this.fromText(text))
    }

    event(e: Event) {
        if (e.kind == 'undone') {
            this.undo(e.to)
            return
        }
        this.at = new Date()
        this.told.push({ event: e, at: this.at })
        this.apply(e)
    }

    private apply(e: Event) {
        if (e.kind == 'decided' || e.kind == 'auto_passed') {
            if (e.decision != this.decision) {
                this.decision = e.decision
                this.lines = 0
            }
            this.resolving = null
        }
        switch (e.kind) {
            case 'decided':
                this.decided(e.seat, e.window, e.choice, e.text, e.source)
                return
            case 'auto_passed':
            case 'draw':
            case 'strike_declared':
                return
            case 'turn':
                this.closeInfluence()
                this.endAction()
                this.current = null
                this.declaring = null
                return
            // Costs and the rules' consequences are left out : the pool a bleed takes is its
            // outcome, what a card does is told on its line
            case 'pool': {
                const action = this.action
                const by = e.cause?.kind == 'effect' ? e.cause.card?.copy : undefined
                const bleeds =
                    action?.bled != null &&
                    by != null &&
                    (by == action.acting || by == action.cardCopy) &&
                    e.delta < 0
                if (action && bleeds) {
                    action.pooled = (action.pooled ?? 0) - e.delta
                    action.pooledSeat = e.seat
                    return
                }
                const amount = { text: `${signed(e.delta)} pool` }
                this.caused(
                    e.cause,
                    e.seat == this.resolving?.entry.seat ?
                        [amount]
                    :   [this.seatSegment(e.seat), { text: ' ' }, amount],
                )
                return
            }
            case 'blood':
                this.caused(e.cause, [
                    this.copySegment(e.minion),
                    { text: ` ${signed(e.delta)} blood` },
                ])
                return
            case 'edge':
                return
            case 'enters_play':
                this.know(e.card)
                if (this.influence && e.minion) {
                    this.influence.out.push(e.card)
                    this.updateInfluence()
                    return
                }
                // The card the line plays : that it is in play goes without saying
                if (e.card.copy != null && e.card.copy == this.currentCopy) {
                    return
                }
                this.tell(
                    e.on != null ?
                        [this.cardSegment(e.card.id), { text: ' on ' }, this.copySegment(e.on)]
                    :   [this.cardSegment(e.card.id, e.card.numbered), { text: ' in play' }],
                )
                return
            case 'leaves_play':
                this.know(e.card)
                // The card the line plays, moved on : it goes without saying
                if (
                    e.card.copy != null &&
                    e.card.copy == this.currentCopy &&
                    !e.contested &&
                    !e.effect
                ) {
                    return
                }
                this.tell([this.cardSegment(e.card.id, e.card.numbered), { text: leavesText(e) }])
                return
            case 'resolved': {
                this.know(e.card)
                const effects = effectsText(e.effects)
                const played = this.played(e.card)
                if (played) {
                    if (effects) {
                        this.append(played, [{ text: effects }], ': ')
                    }
                } else {
                    this.tell([
                        this.cardSegment(e.card.id),
                        ...(effects ? [{ text: `: ${effects}` }] : []),
                    ])
                }
                const entry = played ?? this.current
                if (entry && e.card.copy != null) {
                    this.resolving = { entry, copy: e.card.copy, told: !!effects }
                }
                return
            }
            case 'canceled': {
                this.know(e.card)
                const played = this.played(e.card)
                if (played) {
                    this.append(played, [{ text: 'canceled' }], ': ')
                    return
                }
                this.tell([this.cardSegment(e.card.id), { text: ' canceled' }])
                return
            }
            case 'block_fails': {
                const block = this.blocks.get(e.minion)
                this.blocks.delete(e.minion)
                if (!block) {
                    return
                }
                // No card played in the attempt : it goes without saying
                if (block.parent[block.parent.length - 1] === block.entry) {
                    block.parent.pop()
                    if (this.current === block.entry) {
                        this.current = null
                    }
                    return
                }
                this.append(block.entry, [{ text: 'fails' }])
                return
            }
            // A hand ( or the top of a library, their own or the active Methuselah's ) the user looked at : a top-level line, kept in sight when its action folds
            case 'looked': {
                const cards = e.cards.flatMap((card, i) => [
                    ...(i > 0 ? [{ text: ', ' }] : []),
                    this.cardSegment(card.id),
                ])
                this.line(
                    this.viewer,
                    [
                        ...(e.zone == 'library' && e.seat == this.viewer ?
                            [{ text: 'looks at the top of their library: ' }]
                        : e.zone == 'library' ?
                            [
                                { text: 'looks at the top of ' },
                                this.seatSegment(e.seat),
                                { text: "'s library: " },
                            ]
                        :   [
                                { text: 'looks at ' },
                                this.seatSegment(e.seat),
                                { text: "'s hand: " },
                            ]),
                        ...(cards.length > 0 ? cards : [{ text: 'no card' }]),
                    ],
                    this.root,
                )
                return
            }
            case 'ousted':
                this.line(e.seat, [{ text: 'ousted' }], this.root)
                return
            case 'action_starts':
                this.startAction(e.seat, e.acting, e.action)
                return
            case 'acting_changes':
                this.know(e.acting)
                if (this.action && e.acting.copy != null) {
                    this.action.acting = e.acting.copy
                }
                return
            case 'action_ends': {
                const action = this.action
                if (!action) {
                    return
                }
                const outcome = OUTCOMES[e.outcome] ?? e.outcome
                // A successful bleed tells the pool it took ; a political action's referendum how it
                // went
                if (action.bled != null && e.outcome == 'succeeds') {
                    const from =
                        action.pooledSeat != null && action.pooledSeat != action.bled ?
                            [{ text: ' from ' }, this.seatSegment(action.pooledSeat)]
                        :   []
                    this.append(
                        action.entry,
                        action.pooled ?
                            [{ text: `${action.pooled} pool` }, ...from]
                        :   [{ text: outcome }],
                        ': ',
                    )
                } else if (!(action.referendum && e.outcome == 'succeeds')) {
                    this.append(
                        action.entry,
                        e.outcome == 'blocked' && e.blocker != null ?
                            [{ text: 'blocked by ' }, this.copySegment(e.blocker)]
                        :   [{ text: outcome }],
                        ': ',
                    )
                }
                this.endAction()
                // What follows its end ( the Edge ) : on its line
                this.current = action.entry
                this.currentCopy = action.cardCopy ?? null
                return
            }
            case 'referendum_ends': {
                const outcome =
                    e.outcome == 'canceled' ?
                        'canceled'
                    :   `${e.outcome} ${e.votes_for}–${e.votes_against}`
                if (this.action) {
                    this.action.referendum = true
                    this.append(this.action.entry, [{ text: outcome }], ': ')
                    this.current = this.action.entry
                } else {
                    this.current = this.line(null, [{ text: `The referendum ${outcome}` }])
                }
                return
            }
            case 'combat_begins': {
                const [acting, opposing] = e.minions
                this.combat = this.line(null, [
                    { text: 'Combat: ' },
                    this.copySegment(acting),
                    { text: ' vs ' },
                    this.copySegment(opposing),
                ])
                this.combat.live = true
                this.current = this.combat
                return
            }
            case 'strikes': {
                const side = (i: number): LogSegment[] => {
                    const struck = e.sides[i]
                    return struck ?
                            [this.copySegment(struck.minion), { text: ` ${roundStrike(struck)}` }]
                        :   []
                }
                const range = e.range == 'long' ? ' long' : ''
                this.endingStrike = e.sides.some(side => side.strike?.kind == 'combat_ends')
                this.current = this.line(null, [
                    { text: e.pair > 0 ? `R${e.round}+${range}: ` : `R${e.round}${range}: ` },
                    ...side(0),
                    { text: ' — ' },
                    ...side(1),
                ])
                return
            }
            case 'combat_ends': {
                const combat = this.combat
                if (!combat) {
                    return
                }
                // A strike ending it says so already
                if (!this.endingStrike) {
                    this.append(this.current ?? combat, [{ text: 'combat ends' }])
                }
                this.endingStrike = false
                combat.live = false
                this.combat = null
                this.current = combat
                return
            }
            case 'card_moves': {
                // A card played from a hidden hand : its copy, as it goes to limbo
                const playing = this.playing
                if (
                    playing &&
                    e.to.zone == 'limbo' &&
                    e.card.id == playing.id &&
                    e.card.copy != null
                ) {
                    this.playing = null
                    this.playedAs(e.card.copy, playing.entry)
                }
                if (e.text) {
                    this.tell(this.fromText(e.text, e))
                }
                return
            }
            default:
                // A card moved, torpor, a card revealed… : as the engine tells it
                if (e.text) {
                    this.tell(this.fromText(e.text, e))
                }
        }
    }

    // The pool or blood a card moves as it resolves : on its line
    private caused(cause: Cause | null | undefined, segments: LogSegment[]) {
        const resolving = this.resolving
        if (cause?.kind != 'effect' || !resolving || cause.card?.copy != resolving.copy) {
            return
        }
        this.append(resolving.entry, segments, resolving.told ? ', ' : ': ')
        resolving.told = true
    }

    // The card played's line, once it resolves or is canceled : what follows is told on it
    private played(card: Card): EngineLogEntry | undefined {
        const played = card.copy != null ? this.plays.get(card.copy) : undefined
        if (played && card.copy != null) {
            this.plays.delete(card.copy)
            this.current = played
            this.currentCopy = card.copy
        }
        return played
    }

    // A card played, by its copy : its line
    private playedAs(copy: number, entry: EngineLogEntry) {
        this.plays.set(copy, entry)
        if (this.current === entry) {
            this.currentCopy = copy
        }
    }

    private decided(
        seat: number,
        window: Window,
        choice: OptionView,
        text?: string | null,
        source?: CardRef | null,
    ) {
        const influences = ['transfer', 'withdraw', 'influence_out', 'draw_crypt']
        if (window == 'influence_transfers' && influences.includes(choice.kind)) {
            this.influenced(seat, choice)
            return
        }
        this.closeInfluence()
        if (choice.kind == 'pass') {
            return
        }
        // A strike chosen : its round's line tells it ; a card discarded in the discard phase
        if (window == 'combat_strike' && choice.kind != 'play') {
            return
        }
        if (window == 'discard_phase' && choice.kind == 'discard') {
            return
        }
        // Nothing allocated : nothing done
        if (choice.kind == 'allocate_to' && !choice.amount) {
            return
        }
        const told = text ?? choice.text
        // The terms or effect of a card ( or minion ) chosen, before its action is announced : on
        // its line once it is
        if (source && this.declaring && told) {
            this.declaring.terms.push({ told, choice })
            return
        }
        // Of the card played or the action under way : on its line
        if (source && this.current && this.isCurrent(source) && told) {
            this.append(this.current, this.fromText(lowered(told), choice))
            return
        }
        const declared = choice.effects?.find(effect => effect.kind == 'action' && !!effect.action)
        const kind = choice.kind == 'take_action' ? choice.action : declared?.action
        // An action decided : its line opens as the engine announces it
        if (kind) {
            this.endAction()
            this.declaring = { seat, choice, terms: [] }
            return
        }
        // Not announced ( the declaration failed ) : nothing pending
        this.declaring = null
        if (!source) {
            this.currentCopy = null
        }
        switch (choice.kind) {
            case 'play': {
                const entry = this.line(seat, this.playSegments(choice))
                this.current = entry
                const copy = choice.card?.copy
                if (copy != null) {
                    this.playedAs(copy, entry)
                } else if (choice.card?.id != null) {
                    this.playing = { id: choice.card.id, entry }
                }
                return
            }
            case 'edge_pool':
                this.current = this.line(seat, [{ text: '+1 pool from the Edge' }])
                return
            case 'declare_block': {
                const blocker = choice.minion ?? choice.actor
                const parent = this.container()
                this.current = this.line(
                    seat,
                    [this.copySegment(blocker), { text: ' blocks' }],
                    parent,
                )
                if (blocker != null) {
                    this.blocks.set(blocker, { entry: this.current, parent })
                }
                return
            }
            case 'cast_votes':
                this.current = this.line(seat, [
                    this.copySegment(choice.minion),
                    { text: ` ${votesText(choice)}` },
                ])
                return
            case 'cast_card_vote':
                this.current = this.line(seat, [{ text: `the card's vote: ${votesText(choice)}` }])
                return
            case 'burn_for_vote':
                this.current = this.line(seat, [
                    { text: 'burns ' },
                    this.cardSegment(choice.card?.id),
                    { text: `: 1 ${choice.favor ? 'for' : 'against'}` },
                ])
                return
            case 'cast_edge':
                this.current = this.line(seat, [
                    { text: `burns the Edge: 1 ${choice.favor ? 'for' : 'against'}` },
                ])
                return
            case 'use_ability': {
                const effects = effectsText(choice.effects)
                // A vampire's own ability : the vampire
                const user = choice.user?.kind == 'minion' ? choice.user.copy : undefined
                this.current = this.line(seat, [
                    ...(choice.card ?
                        this.playSegments(choice)
                    :   [
                            this.copySegment(choice.minion ?? user),
                            ...this.targetSegments(choice.target),
                        ]),
                    ...(effects ? [{ text: `: ${effects}` }] : []),
                ])
                // Its card's terms told on its line ( Haqim's Law's discard )
                this.currentCopy = choice.card?.copy ?? null
                return
            }
        }
        // Another card's terms or effect : the card, then the choice
        if (told) {
            this.current = this.line(seat, [
                ...(source ? [this.cardSegment(source.id), { text: ': ' }] : []),
                ...this.fromText(source ? lowered(told) : told, choice),
            ])
        }
    }

    // The card played or the action under way is this card or minion
    private isCurrent(source: CardRef): boolean {
        return (
            source.copy != null &&
            (source.copy == this.currentCopy ||
                source.copy == this.action?.acting ||
                source.copy == this.action?.cardCopy)
        )
    }

    // A card played : "Form of the Cobra [PRO] by Doctor on P3" ( its player told when it is not
    // the action's acting minion )
    private playSegments(choice: OptionView): LogSegment[] {
        const segments = [this.cardSegment(choice.card?.id)]
        const disciplines = (choice.mode?.disciplines ?? [])
            .filter(d => d.discipline != 'action modifier')
            .map(d => (d.superior ? d.discipline.toUpperCase() : d.discipline))
        if (disciplines.length > 0) {
            segments.push({ text: ` [${disciplines.join(' ')}]` })
        }
        if (choice.actor != null && choice.actor != this.action?.acting) {
            segments.push({ text: ' by ' }, this.copySegment(choice.actor))
        }
        segments.push(...this.targetSegments(choice.target))
        return segments
    }

    private targetSegments(target: Target | null | undefined): LogSegment[] {
        if (!target) {
            return []
        }
        return target.kind == 'seat' ?
                [{ text: ' on ' }, this.seatSegment(target.seat)]
            :   [{ text: ' on ' }, this.copySegment(target.copy)]
    }

    // An action announced, decided or forced ( a hunt ) : its line, folding what happens until it
    // ends
    private startAction(seat: number, acting: Card, ref: ActionRef) {
        this.endAction()
        this.know(acting)
        const declared = this.declaring?.seat == seat ? this.declaring : undefined
        const choice = declared?.choice
        this.declaring = null
        const kind = ref.kind
        const segments: LogSegment[] = [
            this.cardSegment(acting.id, acting.numbered),
            { text: ` ${VERBS[kind] ?? actionName(kind)}` },
        ]
        const card = ref.card?.id ?? choice?.card?.id ?? undefined
        // What it is aimed at : the Methuselah bled ; the choice's names an uncontrolled vampire too
        const target = ref.target ?? choice?.target
        const bleedTarget = kind == 'bleed' && target?.kind == 'seat' ? target.seat : undefined
        if (bleedTarget != null) {
            segments.push({ text: ' ' }, this.seatSegment(bleedTarget))
        }
        if (card != null) {
            const named = ['political', 'employ', 'recruit', 'equip', 'equip_found', 'card']
            segments.push({ text: named.includes(kind) ? ' ' : ' with ' }, this.cardSegment(card))
        }
        if (bleedTarget == null) {
            segments.push(...this.targetSegments(target))
        }
        const entry = this.line(seat, segments, this.root)
        entry.live = true
        this.action = {
            entry,
            acting: acting.copy ?? -1,
            referendum: false,
            card,
            cardCopy: ref.card?.copy ?? undefined,
            bled: bleedTarget ?? undefined,
        }
        this.current = entry
        this.currentCopy = ref.card?.copy ?? null
        for (const { told, choice } of declared?.terms ?? []) {
            this.append(entry, this.fromText(lowered(told), choice))
        }
    }

    private endAction() {
        if (this.action) {
            this.action.entry.live = false
        }
        if (this.combat) {
            this.combat.live = false
        }
        this.action = null
        this.combat = null
        this.blocks.clear()
        this.plays.clear()
        this.current = null
        this.currentCopy = null
        this.playing = null
        this.resolving = null
    }

    // A transfer, a blood moved back, a vampire influenced out, a crypt card drawn : on the seat's
    // influence line
    private influenced(seat: number, choice: OptionView) {
        if (this.influence?.seat != seat) {
            this.closeInfluence()
            this.influence = {
                entry: this.line(seat, [], this.root),
                seat,
                pool: new Map(),
                back: new Map(),
                out: [],
                crypt: 0,
            }
        }
        const influence = this.influence
        const copy = choice.target?.kind == 'card' ? choice.target.copy : undefined
        const slot = choice.slot ?? undefined
        const key = copy != null ? `c${copy}` : `s${slot?.seat}.${slot?.slot}`
        const moved = choice.kind == 'transfer' ? influence.pool : influence.back
        if (choice.kind == 'transfer' || choice.kind == 'withdraw') {
            const vampire = moved.get(key) ?? { copy, slot, n: 0 }
            vampire.n++
            moved.set(key, vampire)
        } else if (choice.kind == 'draw_crypt') {
            influence.crypt++
        }
        this.current = null
        this.updateInfluence()
    }

    private updateInfluence() {
        const influence = this.influence
        if (!influence) {
            return
        }
        // Two copies of a vampire ( a crypt holds several ) : told apart by their place
        const named = new Map<string, Set<number>>()
        for (const vampire of [...influence.pool.values(), ...influence.back.values()]) {
            if (vampire.copy != null) {
                const text = this.copySegment(vampire.copy).text
                named.set(text, (named.get(text) ?? new Set()).add(vampire.copy))
            }
        }
        const segment = (vampire: Uncontrolled): LogSegment => {
            const told = this.uncontrolled(vampire)
            const alike = (named.get(told.text)?.size ?? 0) > 1
            return alike && vampire.slot ?
                    { ...told, text: `${told.text} #${vampire.slot.slot + 1}` }
                :   told
        }
        const parts: LogSegment[][] = []
        let first = true
        for (const vampire of influence.pool.values()) {
            const n = vampire.n
            parts.push([{ text: first ? `${n} pool to ` : `${n} to ` }, segment(vampire)])
            first = false
        }
        for (const vampire of influence.back.values()) {
            parts.push([{ text: `${vampire.n} blood back to pool from ` }, segment(vampire)])
        }
        for (const card of influence.out) {
            parts.push([{ text: 'influences out ' }, this.cardSegment(card.id, card.numbered)])
        }
        if (influence.crypt > 0) {
            parts.push([{ text: `${influence.crypt} crypt card${influence.crypt > 1 ? 's' : ''}` }])
        }
        influence.entry.segments = parts.flatMap((part, i) =>
            i == 0 ? part : [{ text: ', ' }, ...part],
        )
        this.changed()
    }

    // A vampire of the uncontrolled region : by its name when the viewer sees it, else by its place
    private uncontrolled(vampire: Uncontrolled): LogSegment {
        if (vampire.copy != null) {
            return this.copySegment(vampire.copy)
        }
        return { text: vampire.slot ? `uncontrolled #${vampire.slot.slot + 1}` : 'uncontrolled' }
    }

    private closeInfluence() {
        this.influence = null
    }

    // Decisions taken back : the log told again up to them, what it told since left out ( a line
    // grown since, an action ended since, a combat begun since )
    private undo(to: number) {
        const cut = this.told.findIndex(
            told =>
                'event' in told &&
                (told.event.kind == 'decided' || told.event.kind == 'auto_passed') &&
                told.event.decision >= to,
        )
        // The driver's own lines stay : its undo's is told before the undo reaches the log
        const kept =
            cut < 0 ?
                this.told
            :   [...this.told.slice(0, cut), ...this.told.slice(cut).filter(told => 'say' in told)]
        this.told = []
        this.decision = 0
        this.lines = 0
        this.influence = null
        this.action = null
        this.combat = null
        this.endingStrike = false
        this.current = null
        this.currentCopy = null
        this.playing = null
        this.blocks.clear()
        this.plays.clear()
        this.declaring = null
        this.resolving = null
        // What the views taught ( names, copies ) is kept : views are not replayed
        this.root.splice(0)
        for (const told of kept) {
            this.told.push(told)
            if ('event' in told) {
                this.at = told.at
                this.apply(told.event)
            } else {
                this.at = told.at
                this.said(told.say.seat, told.say.text)
            }
        }
        this.changed()
    }
}

// A strike in a round's line, with the damage it dealt : "hands (1)", "hands" ( at long range ),
// "dodge", "no strike"
function roundStrike(struck: StruckSide): string {
    if (!struck.strike) {
        return 'no strike'
    }
    return strikeName({ ...struck.strike, n: struck.damage > 0 ? struck.damage : null })
}

// A card leaving play, as the engine tells it : its effect ending, contested, burned, out of play
// with its minion ( Descent into Darkness ), or moved elsewhere
function leavesText(e: Extract<Event, { kind: 'leaves_play' }>): string {
    if (e.effect) {
        return "'s effect ends"
    }
    if (e.contested) {
        return ' is contested'
    }
    if (!e.to) {
        return ' leaves play'
    }
    return e.to == 'ash_heap' ? ' burned' : ` to ${e.to.replace(/_/g, ' ')}`
}

function votesText(choice: OptionView): string {
    const votes = choice.effects?.find(effect => effect.kind == 'votes')
    const n = votes?.n ?? 1
    return `${n} ${choice.favor ? 'for' : 'against'}`
}
